import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { toast } from "@shared/ui/use-toast";
import { uxSettingsApi } from "./api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { queryClient } from "@shared/api/queries/queryClient";

export const useCreateEmailSignature = () => {
  return useSmartMutation(
    (payload: any) => uxSettingsApi.createEmailSignature(payload),
    {
      onSuccess: () => {
        toast({
          title: "Email Signature create",
          description: "Email Signature created successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "emailSignature", "list"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create email signature`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateEmailSignature = (signatureId: string) => {
  return useSmartMutation(
    (payload: any) => uxSettingsApi.updateEmailSignature(payload, signatureId),
    {
      onSuccess: () => {
        toast({
          title: "Email Signature updated",
          description: "Email Signature updated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "emailSignature", "list"],
        });
        queryClient.refetchQueries({
          queryKey: [
            "ux",
            "emailSignature",
            "emailSignatureDetails",
            signatureId,
          ],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update email signature`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetAllEmailSignatures = (
  page?: any,
  limit?: any,
  search?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.settings.emailSignature.getAllEmailSignatures(
      page,
      limit,
      search
    ),
    () => uxSettingsApi.getAllEmailSignatures(utilityId, page, limit, search),
    {
      enabled: !!utilityId,
    }
  );
};

export const useUpdateEmailSignatureStatus = (signatureId: string) => {
  return useSmartMutation(
    (payload: any) =>
      uxSettingsApi.updateEmailSignatureStatus(payload, signatureId),
    {
      onSuccess: () => {
        toast({
          title: "Email Signature status updated",
          description: "Email Signature status updated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "emailSignature", "list"],
        });
        queryClient.refetchQueries({
          queryKey: [
            "ux",
            "emailSignature",
            "emailSignatureDetails",
            signatureId,
          ],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update email signature status`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useDeleteEmailSignature = () => {
  return useSmartMutation(
    (signatureId: string): Promise<any> => {
      return uxSettingsApi.deleteEmailSignature(signatureId);
    },
    {
      onSuccess: () => {
        toast({
          title: "Email Signature deleted",
          description: "Email Signature deleted successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "emailSignature", "list"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to delete email signature",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetEmailSignatures = (
  id: string,
  openDetailEmail?: boolean,
  openCreateEmail?: boolean
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.settings.emailSignature.getEmailSignatures(id),
    () => uxSettingsApi.getEmailSignatures(id, utilityId),
    {
      enabled: !!id,
    }
  );
};

export const useGetAllSegments = (
  page?: any,
  limit?: any,
  search?: any,
  audienceType?: string,
  criteria?: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.settings.segments.getAllSegments(
      page,
      limit,
      search,
      audienceType,
      criteria
    ),
    () =>
      uxSettingsApi.getAllSegments(
        utilityId,
        page,
        limit,
        search,
        audienceType,
        criteria
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useDeleteSegment = () => {
  return useSmartMutation(
    (segmentId: string): Promise<any> => {
      return uxSettingsApi.deleteSegment(segmentId);
    },
    {
      onSuccess: () => {
        toast({
          title: "Segment deleted",
          description: "Segment deleted successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "segments", "list"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to delete segment",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetSegment = (
  id: string,
  openDetailSegment?: boolean,
  action?: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.settings.segments.getSegment(id),
    () => uxSettingsApi.getSegment(id, utilityId),
    {
      enabled: !!id || openDetailSegment || action === "edit",
    }
  );
};

export const useCreateSegment = () => {
  return useSmartMutation(
    (payload: any): Promise<any> => {
      return uxSettingsApi.createSegment(payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Segment created",
          description: "Segment created successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "segments", "list"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create segment",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateSegment = (segmentId: string) => {
  return useSmartMutation(
    (payload: any): Promise<any> => {
      return uxSettingsApi.updateSegment(payload, segmentId);
    },
    {
      onSuccess: () => {
        toast({
          title: "Segment updated",
          description: "Segment updated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "segments", "list"],
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "segments", "segmentDetails"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update segment",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetAllFilterFields = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.settings.segments.getAllFilterFields(),
    () => uxSettingsApi.getFilterFields(utilityId),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetCatSubcatChoices = (name: string, code?: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.settings.segments.choicesList(name, code),
    () => uxSettingsApi.getCatSubCatChoices(name, remoteUtilityId, code)
  );
};

export const useGetPlanChoices = (
  name: string,
  categoryCode?: string,
  subcategoryCode?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.settings.segments.planChoicesList(
      name,
      categoryCode,
      subcategoryCode
    ),
    () =>
      uxSettingsApi.getPlanChoices(
        name,
        remoteUtilityId,
        categoryCode,
        subcategoryCode
      ),
    {
      enabled: !!categoryCode && !!subcategoryCode,
    }
  );
};
